// Pets: adopt at the Pet Shop, keep them fed and happy, and they give a small perk.
// The companion pet follows you around town (render-side).

import { TUNABLES } from "@degen/shared/config/tunables";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { addTrust } from "../player";
import { money } from "@degen/shared/money";

const T = TUNABLES.pets;

export type PetKind = "dog" | "cat" | "frog" | "hamster" | "penguin" | "capybara";
export const PET_KINDS: PetKind[] = ["dog", "cat", "frog", "hamster", "penguin", "capybara"];

export interface Pet {
  id: string;
  kind: PetKind;
  name: string;
  seed: number; // coat color / markings
  happiness: number; // 0-100
  adoptedDay: number;
  lastPettedDay: number;
  sadDays: number;
}

export interface PetsState {
  owned: Pet[];
  companionId: string | null;
  nextId: number;
}

export function createPets(): PetsState {
  return { owned: [], companionId: null, nextId: 1 };
}

export const PET_PERK_TEXT: Record<PetKind, string> = {
  dog: "+10 followers every morning (dog-walk friends)",
  cat: "Heat cools 5 faster per day",
  frog: "Memes get +20% engagement",
  hamster: "Farm tasks earn +10% points",
  penguin: "Half as many scam DMs",
  capybara: "+0.2 trust per day (chill vibes)",
};

/** Happy pets of this kind the player owns (perks don't stack across pets of the same kind). */
export function hasPerk(state: GameState, kind: PetKind): boolean {
  return state.pets.owned.some((p) => p.kind === kind && p.happiness >= T.perkMinHappiness);
}

export function perkAmount(kind: PetKind): number {
  return T.kinds[kind].amount;
}

export type PetResult = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function adoptPet(state: GameState, rng: Rng, kind: PetKind, name: string): PetResult {
  const p = state.player;
  const cfg = T.kinds[kind];
  if (!cfg) return { ok: false, error: "Unknown pet." };
  const clean = name.trim().slice(0, 16);
  if (Array.from(clean).length < 1) return { ok: false, error: "Give your pet a name." };
  if (state.pets.owned.length >= T.maxPets) return { ok: false, error: `Max ${T.maxPets} pets. Your apartment is small.` };
  if (p.cash < cfg.price) return { ok: false, error: `A ${kind} costs ${money(cfg.price)}.` };
  p.cash -= cfg.price;
  const pet: Pet = {
    id: `pet${state.pets.nextId++}`,
    kind,
    name: clean,
    seed: rng.seed(),
    happiness: T.happinessStart,
    adoptedDay: dayOf(state.tick),
    lastPettedDay: 0,
    sadDays: 0,
  };
  state.pets.owned.push(pet);
  state.pets.companionId ??= pet.id;
  pushLog(state, "pet_adopted", `You adopted ${pet.name} the ${kind}! ${PET_PERK_TEXT[kind]}.`);
  return { ok: true };
}

export function feedPet(state: GameState, id: string): PetResult {
  const pet = state.pets.owned.find((x) => x.id === id);
  if (!pet) return { ok: false, error: "No such pet." };
  if (state.player.cash < T.feedCostUsd) return { ok: false, error: `Pet food costs ${money(T.feedCostUsd)}.` };
  if (pet.happiness >= 100) return { ok: false, error: `${pet.name} is full.` };
  state.player.cash -= T.feedCostUsd;
  pet.happiness = Math.min(100, pet.happiness + T.feedHappiness);
  pet.sadDays = 0;
  return { ok: true };
}

export function petPet(state: GameState, id: string): PetResult {
  const pet = state.pets.owned.find((x) => x.id === id);
  if (!pet) return { ok: false, error: "No such pet." };
  const day = dayOf(state.tick);
  if (pet.lastPettedDay === day) return { ok: false, error: `${pet.name} already got pets today. Greedy.` };
  pet.lastPettedDay = day;
  pet.happiness = Math.min(100, pet.happiness + T.petHappiness);
  return { ok: true };
}

export function setCompanion(state: GameState, id: string | null): PetResult {
  if (id !== null && !state.pets.owned.some((x) => x.id === id)) return { ok: false, error: "No such pet." };
  state.pets.companionId = id;
  return { ok: true };
}

/** Day boundary: hunger, perks, and pets that leave if neglected. */
export function petsNewDay(state: GameState) {
  const p = state.player;
  for (const pet of [...state.pets.owned]) {
    pet.happiness = Math.max(0, pet.happiness - T.happinessDecayPerDay);
    if (pet.happiness <= 0) pet.sadDays++;
    if (pet.sadDays >= T.runAwayAfterSadDays) {
      state.pets.owned = state.pets.owned.filter((x) => x !== pet);
      if (state.pets.companionId === pet.id) state.pets.companionId = state.pets.owned[0]?.id ?? null;
      pushLog(state, "pet_left", `${pet.name} went to live with a degen who remembers to buy pet food. 💔`);
    }
  }
  if (hasPerk(state, "capybara")) addTrust(p, perkAmount("capybara"));
}
